import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import styles from "./PublishCard.module.css";
import CheckboxGroup from "shared/@forms/CheckboxGroup";
import DateTimePicker from "shared/@forms/DateTimePicker";
import DigitalDelivery from "shared/@forms/DigitalDelivery";

const STATUS_OPTIONS = [
  { value: "Archived", label: "Archived", color: "#ED1F24" },
  { value: "InReview", label: "In Review", color: "#91B6BB" },
  { value: "Scheduled", label: "Scheduled", color: "#FF9A00" },
  { value: "Published", label: "Published", color: "#43BA32" },
];

function CheckIcon(props) {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <path
        d="M1 5L4 8L9 2"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function PublishCard({
  status = "",
  publishOn,
  unpublishOn,
  showAsCurrentUntil,
  unpublishEnabled = false,
  digitalDeliveryEnabled = false,
  showAsUpcomingOn,
  showDigitalDelivery = true,
  collapseWhenNoStatus = true,
  onChange,
  onStatusChange,
  onPublishOnChange,
  onUnpublishOnChange,
  onUnpublishToggle,
  onDigitalDeliveryChange,
  onShowAsUpcomingOnChange,
  onDigitalDeliveryStateChange,
}) {
  const [digitalDeliveryAutoDerived, setDigitalDeliveryAutoDerived] = useState(
    () =>
      isAutoDerivedDigitalDelivery({
        digitalDeliveryEnabled,
        publishOn,
        showAsCurrentUntil,
        showAsUpcomingOn,
      }),
  );

  const normalizedStatus = normalizeStatus(status);
  const hasSelectedStatus = STATUS_OPTIONS.some(
    (option) => option.value === normalizedStatus,
  );
  const shouldCollapse = collapseWhenNoStatus && !hasSelectedStatus;
  const isPublishOnInactive = ["Archived", "InReview"].includes(
    normalizedStatus,
  );

  useEffect(() => {
    if (!digitalDeliveryEnabled || !digitalDeliveryAutoDerived) {
      return;
    }

    const publishOnDate = toDateValue(publishOn);
    const hasPublishOnDate =
      publishOnDate instanceof Date && !Number.isNaN(publishOnDate.getTime());

    if (!hasPublishOnDate) {
      return;
    }

    const nextShowAsUpcomingOn = toValueObject(addWeeks(publishOnDate, -1));
    const nextShowAsCurrentUntil = toValueObject(addWeeks(publishOnDate, 1));

    const resolvedCurrentUntil = showAsCurrentUntil || "";
    if (
      showAsUpcomingOn === nextShowAsUpcomingOn &&
      resolvedCurrentUntil === nextShowAsCurrentUntil
    ) {
      return;
    }

    onShowAsUpcomingOnChange?.(nextShowAsUpcomingOn);
    onDigitalDeliveryStateChange?.({
      enabled: true,
      showAsUpcomingOn: nextShowAsUpcomingOn,
      showAsCurrentUntil: nextShowAsCurrentUntil,
      autoDerived: true,
    });
    onChange?.({
      digitalDeliveryEnabled: true,
      showAsUpcomingOn: nextShowAsUpcomingOn,
      showAsCurrentUntil: nextShowAsCurrentUntil,
    });
  }, [
    digitalDeliveryAutoDerived,
    digitalDeliveryEnabled,
    onChange,
    onDigitalDeliveryStateChange,
    onShowAsUpcomingOnChange,
    publishOn,
    showAsCurrentUntil,
    showAsUpcomingOn,
    unpublishOn,
  ]);

  return (
    <div
      className={[styles.panel, shouldCollapse ? styles.panelCompressed : ""]
        .filter(Boolean)
        .join(" ")}
    >
      <div className={styles.header}>Publish</div>
      <div className={styles.divider} />

      <div className={styles.statusRow}>
        <span className={styles.statusLabel}>Status</span>
        <div
          className={styles.statusGroup}
          role="group"
          aria-label="Publish status"
        >
          {STATUS_OPTIONS.map((option) => {
            const isActive = normalizedStatus === option.value;
            return (
              <button
                key={option.value}
                type="button"
                className={styles.statusButton}
                style={
                  isActive
                    ? {
                        background: option.color,
                        borderColor: option.color,
                        color: "#FFFFFF",
                      }
                    : { borderColor: option.color, color: option.color }
                }
                onClick={() => {
                  const nextStatus = isActive ? "" : option.value;

                  // If clicking to select "Published" with a future date,
                  // automatically switch to "Scheduled" instead
                  let finalStatus = nextStatus;
                  if (
                    !isActive &&
                    option.value === "Published" &&
                    isFutureDateValue(publishOn)
                  ) {
                    finalStatus = "Scheduled";
                  }

                  onStatusChange?.(finalStatus);
                  onChange?.({ status: toLegacyStatus(finalStatus) });
                }}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      {!shouldCollapse && normalizedStatus !== "Archived" && (
        <div className={styles.grid}>
          <div className={styles.fieldBlock}>
            <label className={styles.label}>Publish On</label>
            <DateTimePicker
              value={toDateValue(publishOn)}
              placeholderValue={
                isPublishOnInactive ? toDateValue(publishOn) : undefined
              }
              onChange={(value) => {
                const normalizedValue = toValueObject(value);
                onPublishOnChange?.(normalizedValue);

                const nextPartial = { publishOn: normalizedValue };
                if (isFutureDateValue(value)) {
                  const scheduledStatus = toLegacyStatus("Scheduled");
                  onStatusChange?.("Scheduled");
                  nextPartial.status = scheduledStatus;
                }

                onChange?.(nextPartial);
              }}
            />
          </div>

          <div className={styles.fieldBlock}>
            <label className={styles.label}>Unpublish On</label>
            <DateTimePicker
              value={toDateValue(unpublishOn)}
              onChange={(value) => {
                const normalizedValue = toValueObject(value);
                onUnpublishOnChange?.(normalizedValue);
                onChange?.({ unpublishOn: normalizedValue });
              }}
              disabled={!unpublishEnabled}
            />
            <CheckboxGroup
              id="publish-unpublish-toggle"
              className={styles.unpublishCheckbox}
              checked={unpublishEnabled}
              onChange={(event) => {
                const enabled = event.target.checked;
                onUnpublishToggle?.(enabled);
                onChange?.({ unpublishEnabled: enabled });
              }}
              icon={<CheckIcon className={styles.checkIcon} />}
              size={18}
            >
              Unpublish on this date
            </CheckboxGroup>
          </div>
        </div>
      )}

      {showDigitalDelivery && (
        <>
          <div className={styles.header}>Digital Delivery</div>
          <div className={styles.divider} />

          <DigitalDelivery
            enabled={digitalDeliveryEnabled}
            showAsCurrentUntil={showAsCurrentUntil}
            showAsUpcomingOn={showAsUpcomingOn}
            autoDerived={digitalDeliveryAutoDerived}
            onChange={(next) => {
              const isToggleOn = next.enabled && !digitalDeliveryEnabled;
              const isToggleOff = !next.enabled && digitalDeliveryEnabled;
              const isManualUpcomingEdit =
                next.enabled &&
                digitalDeliveryEnabled &&
                next.showAsUpcomingOn !== showAsUpcomingOn;
              const isManualCurrentUntilEdit =
                next.enabled &&
                digitalDeliveryEnabled &&
                next.showAsCurrentUntil !== showAsCurrentUntil;

              const publishOnDate = toDateValue(publishOn);
              const hasPublishOnDate =
                publishOnDate instanceof Date &&
                !Number.isNaN(publishOnDate.getTime());

              const defaultShowAsUpcomingOn = hasPublishOnDate
                ? toValueObject(addWeeks(publishOnDate, -1))
                : showAsUpcomingOn || "";
              const defaultShowAsCurrentUntil = hasPublishOnDate
                ? toValueObject(addWeeks(publishOnDate, 1))
                : showAsCurrentUntil || "";

              const nextShowAsUpcomingOn = isToggleOn
                ? defaultShowAsUpcomingOn
                : next.showAsUpcomingOn;
              const nextShowAsCurrentUntil = isToggleOn
                ? defaultShowAsCurrentUntil
                : next.showAsCurrentUntil;

              if (isToggleOn) {
                setDigitalDeliveryAutoDerived(true);
              }

              if (
                isManualUpcomingEdit ||
                isManualCurrentUntilEdit ||
                isToggleOff
              ) {
                setDigitalDeliveryAutoDerived(false);
              }

              onDigitalDeliveryChange?.(next.enabled);
              onShowAsUpcomingOnChange?.(nextShowAsUpcomingOn);
              onDigitalDeliveryStateChange?.({
                ...next,
                showAsUpcomingOn: nextShowAsUpcomingOn,
                showAsCurrentUntil: nextShowAsCurrentUntil,
                autoDerived: isToggleOn
                  ? true
                  : isToggleOff ||
                    isManualUpcomingEdit ||
                    isManualCurrentUntilEdit
                  ? false
                  : digitalDeliveryAutoDerived,
              });
              onChange?.({
                digitalDeliveryEnabled: next.enabled,
                showAsUpcomingOn: nextShowAsUpcomingOn,
                showAsCurrentUntil: nextShowAsCurrentUntil,
              });
            }}
          />
        </>
      )}
    </div>
  );
}

PublishCard.propTypes = {
  status: PropTypes.oneOf([
    "",
    "Archived",
    "InReview",
    "In Review",
    "Scheduled",
    "Published",
  ]),
  publishOn: PropTypes.string,
  unpublishOn: PropTypes.string,
  showAsCurrentUntil: PropTypes.string,
  unpublishEnabled: PropTypes.bool,
  digitalDeliveryEnabled: PropTypes.bool,
  showAsUpcomingOn: PropTypes.string,
  showDigitalDelivery: PropTypes.bool,
  collapseWhenNoStatus: PropTypes.bool,
  onChange: PropTypes.func,
  onStatusChange: PropTypes.func,
  onPublishOnChange: PropTypes.func,
  onUnpublishOnChange: PropTypes.func,
  onUnpublishToggle: PropTypes.func,
  onDigitalDeliveryChange: PropTypes.func,
  onShowAsUpcomingOnChange: PropTypes.func,
  onDigitalDeliveryStateChange: PropTypes.func,
};

export default PublishCard;

function normalizeStatus(value) {
  return value === "In Review" ? "InReview" : value;
}

function toLegacyStatus(value) {
  return value === "InReview" ? "In Review" : value;
}

function toDateValue(value) {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  // Handle ISO 8601 string format
  if (typeof value === "string" && value.trim()) {
    return new Date(value);
  }

  // Handle legacy { date, time } object format for backward compatibility
  if (value?.date) {
    const [year, month, day] = value.date.split("-").map(Number);
    const [hours = 0, minutes = 0] = (value.time || "00:00")
      .split(":")
      .map(Number);
    return new Date(year, month - 1, day, hours, minutes);
  }

  return null;
}

function toValueObject(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) {
    return "";
  }
  // Return ISO 8601 format string
  return value.toISOString();
}

function isAutoDerivedDigitalDelivery({
  digitalDeliveryEnabled,
  publishOn,
  showAsCurrentUntil,
  showAsUpcomingOn,
}) {
  const publishOnDate = toDateValue(publishOn);

  if (
    !digitalDeliveryEnabled ||
    !(publishOnDate instanceof Date) ||
    Number.isNaN(publishOnDate.getTime())
  ) {
    return false;
  }

  return (
    showAsUpcomingOn === toValueObject(addWeeks(publishOnDate, -1)) &&
    showAsCurrentUntil === toValueObject(addWeeks(publishOnDate, 1))
  );
}

function isFutureDateValue(value) {
  const resolvedDate = toDateValue(value);

  if (!(resolvedDate instanceof Date) || Number.isNaN(resolvedDate.getTime())) {
    return false;
  }

  return resolvedDate.getTime() > Date.now();
}

function addWeeks(date, weeks) {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + weeks * 7);
  return next;
}
